import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useBackButtonToPrevRoute } from '@/hooks/useBackButtonToPrevRoute.tsx';
import { routes } from '@/navigation/routes.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { InputContent } from '@/components/common/InputContent/InputContent.tsx';
import {
  blockContainer,
  menuItemStyle,
  menuTextStyle
} from '@/pages/Profile/Settings/styles.ts';
import Toggle from '@/components/common/Toggle/Toggle.tsx';
import {
  deleteProfileButton,
  deleteProfileButtonContainer
} from '@/pages/Profile/Settings/Account/styles.ts';
import usePolicy from '@/hooks/usePolicy.ts';
import Modal from '@/animations/Modal.tsx';
import PaywallPopup from '@/components/Popup/PaywallPopup/PaywallPopup.tsx';
import { PaywallTypes, ProductInvoicePlacements } from '@/enums/apiEnums.ts';
import PremiumBadge from '@/components/Popup/PaywallPopup/components/PremiumBadge.tsx';

const Account: FC = () => {
  useBackButtonToPrevRoute(routes.profileSettings, false);
  const [payWallOpen, setPayWallOpen] = useState(false);

  const { t } = useTranslation();
  const { hideProfile, showProfile, shadowProfile, unshadowProfile, premium } =
    useProfileStore();
  const { isHidden, isInsufficientData, isIncognitoMode } = usePolicy();

  const navigate = useNavigate();

  const handleHideToggle = async (checked: boolean) => {
    try {
      await (checked ? hideProfile() : showProfile());
    } catch (error) {
      /* error */
    }
  };

  const handleShadowToggle = async (checked: boolean) => {
    if (checked && premium.isExpired) {
      return;
    }

    try {
      await (checked ? shadowProfile() : unshadowProfile());
    } catch (error) {
      /* error */
    }
  };

  const handleShadowToggleClick = (isIncognitoEnabled: boolean) => {
    if (!isIncognitoEnabled && premium.isExpired) {
      setPayWallOpen(true);
      return false;
    }
    return true;
  };

  return (
    <div style={{ height: 'calc(100vh - 100px)', paddingTop: '100px' }}>
      <InputContent
        title={t('account_menu_item')}
        footer={
          isInsufficientData
            ? t('hide_profile_switcher_subtitle_shadowed')
            : t('hide_profile_switcher_subtitle')
        }
      >
        <div style={blockContainer}>
          <div style={menuItemStyle}>
            <div style={{ flexGrow: 1, textAlign: 'left' }}>
              <span style={menuTextStyle}>
                {t('hide_profile_switcher_title')}
              </span>
            </div>
            <Toggle
              disabled={isInsufficientData}
              isChecked={isHidden}
              onChange={(checked) => handleHideToggle(checked)}
            />
          </div>
        </div>
      </InputContent>
      <InputContent
        style={{ marginTop: 20 }}
        footer={
          isInsufficientData
            ? t('hide_profile_switcher_subtitle_incognito_shadowed')
            : t('hide_profile_switcher_subtitle_incognito')
        }
      >
        <div style={blockContainer}>
          <div style={menuItemStyle}>
            <div style={{ flexGrow: 1, textAlign: 'left' }}>
              <span style={menuTextStyle}>{t('incognito_mode_title')}</span>
              <PremiumBadge text="Premium" />
            </div>
            <Toggle
              disabled={isInsufficientData}
              isChecked={isIncognitoMode}
              onClick={() => handleShadowToggleClick(isIncognitoMode)}
              onChange={(checked) => handleShadowToggle(checked)}
            />
          </div>
        </div>
      </InputContent>
      <div style={deleteProfileButtonContainer}>
        <div
          style={deleteProfileButton}
          onClick={() => navigate(routes.profileHideAccount)}
        >
          {t('button.delete_profile')}
        </div>
      </div>
      <Modal
        fullScreen
        scrollable
        open={payWallOpen}
        handleClose={() => setPayWallOpen(false)}
      >
        <PaywallPopup
          handleClose={() => setPayWallOpen(false)}
          type={PaywallTypes.TYPE_SUBSCRIPTION}
          placement={ProductInvoicePlacements.PROFILE_SETTINGS_SWITCHERS}
          hideBackButton={false}
          prevHeaderTemplate="light"
        />
      </Modal>
    </div>
  );
};

export default Account;
